import { toast } from 'react-toastify';

/**
 * Turn an app-relative path into an absolute URL so shared links work
 * outside the app. Already-absolute URLs pass through untouched.
 */
export function toAbsoluteShareUrl(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

/**
 * Share via the device share sheet when available (mobile), otherwise
 * copy "text + url" to the clipboard (desktop fallback). Owns the
 * fallback toasts so every share button behaves the same.
 *
 * @returns {'shared'|'cancelled'|'copied'|'failed'}
 */
export async function shareOrCopy({ title, text, url, copiedMessage = 'Copied! Paste it anywhere to share.' }) {
    const shareUrl = toAbsoluteShareUrl(url);
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
            await navigator.share({ title, text, url: shareUrl });
            return 'shared';
        } catch (err) {
            if (err?.name === 'AbortError') return 'cancelled';
            // Some in-app browsers reject share() outright; fall through to copy.
        }
    }
    return copyToClipboard([text, shareUrl].filter(Boolean).join('\n'), copiedMessage);
}

/**
 * Copy a value to the clipboard with a success/error toast.
 *
 * @returns {'copied'|'failed'}
 */
export async function copyToClipboard(value, message = 'Link copied.') {
    try {
        if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
        await navigator.clipboard.writeText(value);
        toast.success(message);
        return 'copied';
    } catch {
        toast.error('Could not copy. Please copy the page link from the address bar.');
        return 'failed';
    }
}
